// @ts-ignore
import * as tj from '@mapbox/togeojson';
// @ts-ignore
import tokml from 'tokml';
import { DOMParser, XMLSerializer } from 'xmldom';

type GpxDocument = ReturnType<InstanceType<typeof DOMParser>['parseFromString']>;

export class InvalidGpxError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidGpxError';
  }
}

// xmldom 0.6 does not disable DOCTYPE/external entities. Strip them before
// parsing so a crafted GPX can't trigger entity expansion (billion laughs)
// or SYSTEM-entity probing.
const DOCTYPE_RE = /<!DOCTYPE[\s\S]*?>/gi;
const ENTITY_RE = /<!ENTITY[\s\S]*?>/gi;

export interface KmlStyleOptions {
  trackColor: string;
  trackWidth: number;
  trackOpacity: number;
  waypointColor: string;
  waypointScale: number;
  altitudeMode: 'clampToGround' | 'relativeToGround' | 'absolute';
  includeWaypoints: boolean;
  includeTracks: boolean;
  includeRoutes: boolean;
}

export const DEFAULT_KML_STYLE_OPTIONS: KmlStyleOptions = {
  trackColor: '#2563EB',
  trackWidth: 3,
  trackOpacity: 1.0,
  waypointColor: '#EA580C',
  waypointScale: 1.2,
  altitudeMode: 'clampToGround',
  includeWaypoints: true,
  includeTracks: true,
  includeRoutes: true,
};

function hexToKmlColor(hex: string, opacity: number = 1): string {
  const r = hex.slice(1, 3).toLowerCase();
  const g = hex.slice(3, 5).toLowerCase();
  const b = hex.slice(5, 7).toLowerCase();
  const aa = Math.round(opacity * 255).toString(16).padStart(2, '0');
  return `${aa}${b}${g}${r}`;
}

function injectSimplestyle(geoJson: any, options: KmlStyleOptions): any {
  return {
    ...geoJson,
    features: geoJson.features.map((feature: any) => {
      const type = feature.geometry?.type;
      if (type === 'LineString' || type === 'MultiLineString') {
        return {
          ...feature,
          properties: {
            ...feature.properties,
            stroke: options.trackColor,
            'stroke-width': options.trackWidth,
            'stroke-opacity': options.trackOpacity,
          },
        };
      }
      if (type === 'Point') {
        return {
          ...feature,
          properties: {
            ...feature.properties,
            'marker-color': options.waypointColor,
          },
        };
      }
      return feature;
    }),
  };
}

function applyKmlStyles(kml: string, options: KmlStyleOptions): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(kml, 'text/xml');
  if (!doc.documentElement) {
    throw new Error('applyKmlStyles: failed to parse KML output');
  }
  const serializer = new XMLSerializer();

  const WAYPOINT_ICON = 'http://maps.google.com/mapfiles/kml/paddle/wht-blank.png';

  const iconStyles = doc.getElementsByTagName('IconStyle');
  for (let i = 0; i < iconStyles.length; i++) {
    const iconStyle = iconStyles[i];

    const existingScale = iconStyle.getElementsByTagName('scale');
    while (existingScale.length > 0) {
      iconStyle.removeChild(existingScale[0]);
    }
    const scale = doc.createElement('scale');
    scale.textContent = String(parseFloat(options.waypointScale.toFixed(2)));
    iconStyle.appendChild(scale);

    const existingColor = iconStyle.getElementsByTagName('color');
    while (existingColor.length > 0) {
      iconStyle.removeChild(existingColor[0]);
    }
    const color = doc.createElement('color');
    color.textContent = hexToKmlColor(options.waypointColor);
    iconStyle.appendChild(color);

    let icon = iconStyle.getElementsByTagName('Icon')[0];
    if (!icon) {
      icon = doc.createElement('Icon');
      iconStyle.insertBefore(icon, iconStyle.firstChild);
    }
    const existingHref = icon.getElementsByTagName('href');
    while (existingHref.length > 0) {
      icon.removeChild(existingHref[0]);
    }
    const href = doc.createElement('href');
    href.textContent = WAYPOINT_ICON;
    icon.appendChild(href);
  }

  for (const tag of ['Point', 'LineString'] as const) {
    const elements = doc.getElementsByTagName(tag);
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i];
      const existing = el.getElementsByTagName('altitudeMode');
      while (existing.length > 0) {
        el.removeChild(existing[0]);
      }
      const altMode = doc.createElement('altitudeMode');
      altMode.textContent = options.altitudeMode;
      el.appendChild(altMode);
    }
  }

  return serializer.serializeToString(doc);
}

function stripGpxElements(
  gpxDom: GpxDocument,
  flags: { includeWaypoints: boolean; includeTracks: boolean; includeRoutes: boolean }
): void {
  const root = gpxDom.documentElement;
  if (!root) return;
  const tagsToRemove: string[] = [];
  if (!flags.includeWaypoints) tagsToRemove.push('wpt');
  if (!flags.includeTracks) tagsToRemove.push('trk');
  if (!flags.includeRoutes) tagsToRemove.push('rte');
  for (const tag of tagsToRemove) {
    const elements = root.getElementsByTagName(tag);
    for (let i = elements.length - 1; i >= 0; i--) {
      elements[i].parentNode?.removeChild(elements[i]);
    }
  }
}

export function convertGpxToKml(gpx: string, options: KmlStyleOptions = DEFAULT_KML_STYLE_OPTIONS): string {
  if (typeof gpx !== 'string' || !gpx.trim()) {
    throw new InvalidGpxError('This file is empty.');
  }
  if (!options.includeWaypoints && !options.includeTracks && !options.includeRoutes) {
    throw new InvalidGpxError('Select at least one of Waypoints, Track, or Routes.');
  }
  const sanitized = gpx.replace(DOCTYPE_RE, '').replace(ENTITY_RE, '');
  const gpxDom = new DOMParser().parseFromString(sanitized);
  stripGpxElements(gpxDom, options);
  const geoJson = tj.gpx(gpxDom);
  if (!geoJson?.features?.length) {
    throw new InvalidGpxError('No tracks, routes, or waypoints found in this GPX file.');
  }
  const styled = injectSimplestyle(geoJson, options);
  const kml = tokml(styled, { simplestyle: true });
  return applyKmlStyles(kml, options);
}

export function createObjectUrl(content: string, type: string = 'application/octet-stream'): string {
  const blob = new Blob([content], { type });
  return URL.createObjectURL(blob);
}
